import { ActivityCheckout, ActivityCheckoutSkeleton } from "@components";
import { UrlUtils, RootState, AppDispatch } from '@share/utils';
import { DETAIL_LABEL, DATES_LABEL, SESSION_KEY_LABEL, Routes } from "@share/constants";
import { ActivityDates, DetailLabel } from "@components";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "@share/utils";
import { useDispatch, useSelector } from "react-redux";

import { useEffect, useMemo } from "react";
import { getActivityDetails, activityAvailabilityActions, getActivityQuestions, getActivityConditionalQuestions } from "@share/store/slices";
import { getActivityTaxesTotal } from "@utils";

import { ISessionKey } from "@share/common-types";


export const ActivityCheckoutPage = () => {
  const { id, ratekey } = useParams<{ id: string; ratekey: string }>();

  const values = useMemo(() => UrlUtils.getValues(), []);

  const dispatch = useDispatch<AppDispatch>();

  const location = useLocation();

  const navigate = useNavigate();

  const { activity, loading: detailsLoading } = useSelector((state: RootState) => state.activityDetailsStore);
  const { data: availabilityData, loading: availabilityLoading } = useSelector((state: RootState) => state.activityAvailabilityStore);
  const loginStore = useSelector((s: RootState) => s.loginStore);
  const marginatorStore = useSelector((state: RootState) => state.marginatorStore);
  const { questionData, conditionalData } = useSelector((state: RootState) => state.activityQuestionStore);

  const sessionKey = values[SESSION_KEY_LABEL] as ISessionKey;
  const { account } = loginStore;
  
  useEffect(() => {
    if (activity === null && id && sessionKey && !detailsLoading) {
      dispatch(
        getActivityDetails({
          ActivityCode: id,
          sessionKey: sessionKey,
          isStatic: false,
        })
      );
    }
  }, [activity, id, sessionKey?.value, dispatch]);

  useEffect(() => {
    if (availabilityData === null && id && !availabilityLoading) {
      dispatch(activityAvailabilityActions.checkAvailability({
        ActivityCode: id
      }));
    }
  }, [availabilityData, id, dispatch]);

  useEffect(() => {
    if (questionData === null) {
      dispatch(getActivityQuestions());
    }
  }, [questionData, dispatch]);

  useEffect(() => {
    if (conditionalData === null) {
      dispatch(getActivityConditionalQuestions());
    }
  }, [conditionalData, dispatch]);

  const detailLabelFromStore = useMemo(() => {
    
    if (!activity || !activity.modalities || !ratekey) return null;

    const detailRates = activity.modalities.flatMap(m =>
      m.rates.map(r => ({ ...r, modality: m }))
    );

    let selectedRate = detailRates.find(r => r.rateKey === ratekey);

    if (!selectedRate && availabilityData?.modalities) {
      const availabilityRates = availabilityData.modalities.flatMap((m: any) =>
        m.rates.map((r: any) => ({ ...r, modality: m }))
      );
      selectedRate = availabilityRates.find((r: any) => r.rateKey === ratekey);
    }

    const getMinPublicPrice = (): number => {
      const publicPrices = selectedRate?.publicPrices;
      if (publicPrices && publicPrices.length) {
        return Math.min(...publicPrices.map(({ price }) => price));
      }
      return 0;
    };

    if (selectedRate) {
      const selectedModality = selectedRate?.modality;
      const value = marginatorStore.value || 0;
      const price = selectedRate?.price || 0;
      const percents = 100;

      const marginatorPrice = price / (1 - value / 100);
      const marginatorSavings = selectedRate?.saving / (1 - value / 100);

      const pubPriceNum = getMinPublicPrice();

      const msave = pubPriceNum
        ? account?.breakdownTaxes
          ? (1 - marginatorSavings / pubPriceNum) * percents
          : (marginatorPrice * percents) / pubPriceNum
        : 100;
      const marginatorsavePercents = Math.floor(percents - msave);

      return {
        sessionId: sessionKey.value,
        rateKey: ratekey,
        activityCode: id,
        price: price,
        priceWithoutTaxes: selectedRate?.priceWithoutTaxes,
        taxes: getActivityTaxesTotal(selectedRate?.taxes),
        savings: selectedRate?.saving,
        pubPrice: pubPriceNum,
        marginatorPrice: marginatorPrice,
        marginatorSavings: marginatorSavings,
        marginatorsavePercents: marginatorsavePercents,
        name: activity?.name,
        mediaImages: activity?.content?.mediaImages,
        refundable: selectedRate?.refundable,
        duration: selectedModality ? `${selectedModality.durationValue} ${selectedModality.durationMetric}` : "",
        time: selectedRate?.sessions?.[0]?.name || "",
        currency: activity?.currency,
        review: activity?.reviews?.total || 0,
        rating: activity.reviews?.averageRating || 0,
        questions: selectedModality?.questions,
        languageGuides: selectedModality?.languageGuides,
        startingPoints: activity?.content?.startingPoints,
        travelerPickups: activity?.content?.travelerPickups,
        allowCustomTravelerPickup: activity?.content?.allowCustomTravelerPickup,
        maxWalletClientCash: selectedRate?.maxWalletClientCash,
        maxRewardWalletAmount: (selectedRate as { maxRewardWalletAmount?: number })?.maxRewardWalletAmount,
        isEligibleForRewardWallet: (selectedRate as { isEligibleForRewardWallet?: boolean })?.isEligibleForRewardWallet,
        cancellationPolicies: selectedRate?.cancellationPolicies,
        cancellationPolicyText: selectedRate?.cancellationPolicyText,
        ageRange: activity?.ageRange,
        ageBands: activity?.ageBands,
      } as DetailLabel;
    }
  }, [activity, availabilityData, ratekey, marginatorStore]);

  const finalDetailLabel = (values[DETAIL_LABEL] as DetailLabel) || detailLabelFromStore;
  
  useEffect(() => {
    if (activity && !finalDetailLabel && !detailsLoading && !availabilityLoading) {
      const searchSuffix = (location.search || '').replace(/^\?\?/, '?');
      const detailUrl = `/${account?.name}${Routes.ActivityDetail}/${id}${searchSuffix}`;
      navigate(detailUrl);
    }
  }, [activity, finalDetailLabel, detailsLoading, availabilityLoading, location.search, account?.name, id, navigate]);

  if (!finalDetailLabel && (detailsLoading || availabilityLoading)) {
    return <ActivityCheckoutSkeleton />;
  }

  return (
    <>
      {finalDetailLabel && (
        <ActivityCheckout
          detailLabel={finalDetailLabel}
          dates={values[DATES_LABEL] as ActivityDates}
        />
      )}
    </>
  )
}

